import { call, cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  numberToGermanNumberFormattedString,
} from '@ps20-frontend/frontend-models';
import { Feature, RsVermietungDruckdaten, SpartenType, VorschaedenDruck } from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import {
  getProduktlinienDruck,
  mapLinienpreis,
  mapVermieteteObjekte,
} from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState, Rechtsstreitigkeiten } from '../appState';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';

export function* watchDruckdatenRequested() {
  yield takeEvery(
    GlobalActions.DRUCKDATEN_REQUESTED,
    safeSaga(onDruckdatenRequested, App.RS_VERMIETUNG),
  );
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.RS_VERMIETUNG)) {
      yield cancel();
      return;
    }

    const state: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();

    const rsVermietungDruckdaten: RsVermietungDruckdaten = yield call(mapDruckdaten, state);
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().druckdatenGenerated(rsVermietungDruckdaten),
    );
  } catch (e) {
    PS20Logger.error(App.RS_VERMIETUNG, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

export function mapDruckdaten(state: IAppState): RsVermietungDruckdaten {
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  const druckdaten: RsVermietungDruckdaten = {
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    treuebonus: state.taasInfo.tbNachlass,
    zaehlstuecke: state.rabattstufe,
    spartenType: SpartenType.RECHTSSCHUTZ_VERMIETUNG,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.RS_VERMIETUNG,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.RECHTSSCHUTZ_VERMIETUNG),
    vorschaedenDruck: mapVorschaeden(state.rechtsstreitigkeiten),
    vorversicherungenDruck: null,
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse: '',
    familienstand: state.versicherungsnehmer.familienstand,
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    beibehalten: state.vertragsoptionen.beibehalten,
    nachlass: ausgewaehlteProduktlinie.nachlass,
    zusatzoptionenDruck: [],
    wartezeitverzichtDruck: null,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };

  const vermieteteObjekte = state.vermieteteCardObjekte;
  if (vermieteteObjekte != null && vermieteteObjekte.length > 0) {
    druckdaten.vermieteteObjekte = mapVermieteteObjekte(vermieteteObjekte);
  }

  return druckdaten;
}

function mapVorschaeden(rechtsstreitigkeiten: Rechtsstreitigkeiten): VorschaedenDruck {
  return {
    rechtsstreit: rechtsstreitigkeiten.anzahl,
  };
}
